// Zentrale Navigations-/Menü-Konfiguration + Berechtigungs-Filter.
// Wird sowohl von der Sidebar als auch vom Landing-Redirect (App) genutzt,
// damit die Sichtbarkeitslogik an einer Stelle liegt.

export interface SubMenuItem {
  path: string;
  label: string;
  permission?: string;
  /** Alternativ-Berechtigungen: Eintrag sichtbar, wenn der Nutzer eine davon besitzt
   *  (zusätzlich zu `permission`). So erscheint ein Menüpunkt auch bei Aktionsrechten,
   *  nicht nur bei der "View"-Berechtigung. */
  anyOf?: string[];
  /** Schlüssel für "Neu seit dem letzten Besuch"-Badge (localStorage-Lookup). */
  newBadgeKey?: 'news' | 'events';
}

export interface MenuItem {
  path?: string;
  label: string;
  icon: string;
  permission?: string;
  /** siehe SubMenuItem.anyOf */
  anyOf?: string[];
  subItems?: SubMenuItem[];
}

export const menuItems: MenuItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: '🏠', permission: 'Dashboard.View' },
  {
    label: 'Neue Person(en)',
    icon: '➕',
    permission: 'Persons.Create',
    subItems: [
      { path: '/personen/neu', label: 'Einzelperson', permission: 'Persons.Create' },
      { path: '/personen/familie-anlegen', label: 'Familie', permission: 'Persons.Create' },
      { path: '/registrations', label: 'Online-Anmeldungen', permission: 'Persons.Create' },
    ],
  },
  { path: '/personen', label: 'Personen', icon: '👥', permission: 'Persons.View', anyOf: ['Persons.Create', 'Persons.Edit', 'Persons.Delete', 'Persons.Export'] },
  { path: '/kurse', label: 'Kurse', icon: '📚', permission: 'Courses.View', anyOf: ['Courses.Create', 'Courses.Edit', 'Courses.Delete', 'Courses.Participants.View', 'Courses.Participants.Edit'] },
  { path: '/kursanmeldung', label: 'Kursanmeldung', icon: '🎿', permission: 'Booking.View', anyOf: ['Booking.Manage'] },
  { path: '/uebungsleiter-zeiten', label: 'ÜL-Zeiterfassung', icon: '⏱️', permission: 'Coach.TimeTracking.ManageAll' },
  { path: '/abgeschlossene-zeiten', label: 'Abgeschlossene Zeiterfassungen', icon: '✅', permission: 'Coach.TimeTracking.ManageAll' },
  {
    label: 'Sportstätten',
    icon: '🏟️',
    permission: 'Settings.View',
    subItems: [
      { path: '/sportstaetten', label: 'Übersicht', permission: 'Settings.View' },
      { path: '/sportstaetten/belegung', label: 'Belegungsplanung', permission: 'Settings.View' },
    ],
  },
  { path: '/zahlungen', label: 'Zahlungen', icon: '💰', permission: 'Payments.View', anyOf: ['Payments.Create', 'Payments.Edit', 'Payments.Delete', 'Payments.DirectDebit.View', 'Payments.DirectDebit.Create', 'Payments.DirectDebit.Edit', 'Payments.DirectDebit.Export'] },
  {
    label: 'Club-App',
    icon: '📱',
    permission: 'Settings.View',
    subItems: [
      { path: '/teams', label: 'Teams/Gruppen', permission: 'Settings.View' },
      { path: '/club-app/termine', label: 'Termine', permission: 'Settings.View', newBadgeKey: 'events' },
      { path: '/club-app/beitraege', label: 'Beiträge', permission: 'Settings.View', newBadgeKey: 'news' },
      { path: '/club-app/einladungen', label: 'Einladungen', permission: 'Settings.View' },
      { path: '/club-app/minderjaehrige', label: 'Minderjährige (DSGVO)', permission: 'Settings.View' },
      { path: '/club-app', label: 'Benutzer', permission: 'Settings.View' },
    ],
  },
  { path: '/email', label: 'E-Mail', icon: '✉️', permission: 'Email.Client' },
  { path: '/email-konten', label: 'E-Mail-Konten', icon: '📧', permission: 'EmailAccount.Manage' },
  { path: '/reports', label: 'Reports', icon: '📊', permission: 'Report.View', anyOf: ['Report.Create', 'Report.Export'] },
  { path: '/textverwaltung', label: 'Textverwaltung', icon: '📝', permission: 'Templates.View', anyOf: ['Templates.Create', 'Templates.Edit', 'Templates.Delete'] },
  { path: '/website', label: 'Website', icon: '🌐', permission: 'Settings.View' },
  {
    label: 'Administration',
    icon: '⚙️',
    permission: 'Settings.View',
    subItems: [
      { path: '/administration', label: 'Stammdaten', permission: 'Settings.View' },
      { path: '/administration/iban-pruefung', label: 'IBAN-Prüfung', permission: 'Settings.View' },
    ],
  },
  {
    label: 'Administratoreinstellungen',
    icon: '🔧',
    permission: 'Admin.View',
    // Auch mit dem Einzelrecht Admin.DataCheck sichtbar (SuperAdmin hat '*').
    anyOf: ['Admin.DataCheck'],
    subItems: [
      { path: '/admin/users', label: 'Benutzer', permission: 'Admin.Users.View' },
      { path: '/admin/roles', label: 'Rollen', permission: 'Admin.Roles.View' },
      { path: '/admin/permissions', label: 'Berechtigungen', permission: 'Admin.Permissions.View' },
      { path: '/admin/datenpruefung', label: 'Datenprüfung', permission: 'Admin.DataCheck' },
    ],
  },
];

/**
 * Prüft, ob ein Menüeintrag mit den gegebenen Berechtigungen sichtbar ist.
 * Sichtbar bei: keine Berechtigung nötig, Wildcard '*', exakte `permission`
 * ODER eine der `anyOf`-Aktionsberechtigungen.
 */
export function hasAccess(permission: string | undefined, anyOf: string[] | undefined, permissions: string[]): boolean {
  if (!permission && (!anyOf || anyOf.length === 0)) return true;
  if (permissions.includes('*')) return true; // Fallback: alles anzeigen
  if (permissions.length === 0) return false;
  if (permission && permissions.includes(permission)) return true;
  if (anyOf && anyOf.some((p) => permissions.includes(p))) return true;
  return false;
}

/**
 * Liefert die für die Berechtigungen sichtbaren Menüpunkte (inkl. gefilterter Sub-Items).
 * Gruppen ohne sichtbare Sub-Items werden entfernt.
 */
export function filterMenuItems(permissions: string[]): MenuItem[] {
  return menuItems
    .filter((item) => hasAccess(item.permission, item.anyOf, permissions))
    .map((item) => {
      if (item.subItems) {
        const filteredSubItems = item.subItems.filter((subItem) =>
          hasAccess(subItem.permission, subItem.anyOf, permissions)
        );
        return { ...item, subItems: filteredSubItems };
      }
      return item;
    })
    .filter((item) => !item.subItems || item.subItems.length > 0);
}

/**
 * Bestimmt die Startseite nach dem Login.
 * Hat der Nutzer nur EINEN sichtbaren Menüpunkt, wird direkt dessen Seite
 * angesteuert (statt des Dashboards); sonst das Dashboard.
 */
export function resolveLandingPath(permissions: string[]): string {
  const visible = filterMenuItems(permissions);
  if (visible.length === 1) {
    const only = visible[0];
    if (only.path) return only.path;
    if (only.subItems && only.subItems.length > 0) return only.subItems[0].path;
  }
  return '/dashboard';
}
